import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import StoreOrdersPanel from '@/components/StoreOrdersPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Loader2, Plus, Pencil, Trash2, ExternalLink, Copy, Video, CreditCard, CheckCircle, AlertCircle,
  Upload, ImageIcon, BarChart3, Users, TrendingUp, ChevronLeft, ChevronRight, ChevronDown, Camera, Radio, Layers, Package,
} from 'lucide-react';
import { PayoutCountrySelect, defaultPayoutCountry } from '@/components/payouts/PayoutCountrySelect';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useAuthContext } from '@/contexts/AuthContext';
import { useSubscription } from '@/hooks/useSubscription';
import {
  fetchMyCreatorPage, updateCreatorPage, fetchMyProducts, deleteProduct, fetchSeriesProjects,
  startConnectOnboarding, fetchConnectStatus, getConnectDashboardUrl, fetchCreatorSales, setPayoutSchedule,
  type CreatorPage, type CreatorProduct, type ConnectStatus, type SalesDashboard, type SeriesProject,
} from '@/services/creatorApi';
import { CreateTypeGrid } from '@/components/creation-flow/CreateTypeCards';
import CreatorEmailSetup from '@/components/CreatorEmailSetup';

const MyStorePage: React.FC = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getAuthHeaders } = useAuth();
  const { isAuthenticated } = useAuthContext();
  const { isSubscriber, canSellProducts, loading: subLoading, activateCreator } = useSubscription();

  const [page, setPage] = useState<CreatorPage | null>(null);
  const [products, setProducts] = useState<CreatorProduct[]>([]);
  // In-development series (series_projects) — drafts that aren't listings yet.
  const [seriesDrafts, setSeriesDrafts] = useState<SeriesProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [connectStatus, setConnectStatus] = useState<ConnectStatus | null>(null);
  const [payoutCountry, setPayoutCountry] = useState<string | null>(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [schedSaving, setSchedSaving] = useState<string | null>(null);
  const [sales, setSales] = useState<SalesDashboard | null>(null);

  // Page form
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [bannerImage, setBannerImage] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Collapsible secondary sections
  const [payoutsOpen, setPayoutsOpen] = useState(false);
  const [storefrontOpen, setStorefrontOpen] = useState(
    typeof window !== 'undefined' && window.location.hash === '#storefront'
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [pageData, productsData, connectData, salesData, seriesData] = await Promise.all([
        fetchMyCreatorPage(), fetchMyProducts(), fetchConnectStatus().catch(() => null), fetchCreatorSales().catch(() => null), fetchSeriesProjects().catch(() => [])
      ]);
      setPage(pageData);
      setProducts(productsData);
      // Only show projects that haven't been published to a listing yet.
      setSeriesDrafts((seriesData || []).filter(p => !p.productId));
      if (connectData) setConnectStatus(connectData);
      if (salesData) setSales(salesData);
      // No storefront means nothing this creator publishes can appear in the
      // marketplace, which lists stores rather than products. Open the section
      // that fixes it rather than leaving it collapsed behind a summary line.
      if (!pageData || !String(pageData.displayName || '').trim()) setStorefrontOpen(true);
      if (pageData) {
        setDisplayName(pageData.displayName || '');
        setBio(pageData.bio || '');
        setImageUrl(pageData.imageUrl || '');
        setBannerImage(pageData.bannerImage || '');
        setSlugInput(pageData.slug || '');
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAuthenticated) loadData(); }, [isAuthenticated, loadData]);

  useEffect(() => {
    if (searchParams.get('connect') === 'complete') {
      toast({ title: 'Stripe setup complete', description: 'Checking your account status...' });
      fetchConnectStatus().then(s => setConnectStatus(s)).catch(() => {});
      setPayoutsOpen(true);
    }
  }, [searchParams]);

  const savePage = async () => {
    if (!displayName.trim()) { setFormErrors(e => ({ ...e, displayName: 'Name is required' })); return; }
    setSaving(true);
    try {
      const updated = await updateCreatorPage({ displayName, bio, imageUrl, bannerImage, slug: slugInput || undefined });
      setPage(updated);
      setSlugInput(updated.slug);
      toast({ title: 'Storefront saved' });
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!confirm('Delete this product? Existing purchases will still have access.')) return;
    try {
      await deleteProduct(id);
      setProducts(prev => prev.filter(p => p._id !== id));
      toast({ title: 'Product deleted' });
    } catch (err: any) {
      toast({ title: 'Delete failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Please select an image file', variant: 'destructive' }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Image must be under 5MB', variant: 'destructive' }); return; }
    setUploadingImage(true);
    try {
      const urlRes = await fetch('/api/get-upload-url', {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, fileType: file.type }),
      });
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, s3Key, downloadUrl } = await urlRes.json();
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!putRes.ok) throw new Error('Upload failed');
      setImageUrl(downloadUrl || s3Key);
      toast({ title: 'Image uploaded' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingImage(false); }
  };

  // Banner upload — persists immediately (the banner block lives at the top of the
  // hub, decoupled from the Storefront settings save). Requires a display name.
  const persistBanner = async (newBanner: string) => {
    if (!displayName.trim()) {
      setBannerImage(newBanner);
      toast({ title: 'Add your display name', description: 'Set it in Storefront & sales, then save to keep this banner.' });
      return;
    }
    try {
      const updated = await updateCreatorPage({ displayName, bio, imageUrl, bannerImage: newBanner, slug: slugInput || undefined });
      setPage(updated);
      setBannerImage(updated.bannerImage || '');
    } catch (err: any) {
      setBannerImage(newBanner);
      toast({ title: 'Banner save failed', description: err.message, variant: 'destructive' });
    }
  };

  const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast({ title: 'Please select an image file', variant: 'destructive' }); return; }
    if (file.size > 5 * 1024 * 1024) { toast({ title: 'Image must be under 5MB', variant: 'destructive' }); return; }
    setUploadingBanner(true);
    try {
      const urlRes = await fetch('/api/get-upload-url', {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName: file.name, fileType: file.type }),
      });
      if (!urlRes.ok) throw new Error('Failed to get upload URL');
      const { uploadUrl, s3Key, downloadUrl } = await urlRes.json();
      const putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
      if (!putRes.ok) throw new Error('Upload failed');
      await persistBanner(downloadUrl || s3Key);
      toast({ title: 'Banner updated' });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingBanner(false); e.target.value = ''; }
  };

  const copyStoreUrl = () => {
    if (page?.slug) {
      navigator.clipboard.writeText(`https://plus.heyjoe.io/creators/${page.slug}`);
      toast({ title: 'Link copied' });
    }
  };

  // Show the readable slug form, not the opaque ownCommunityRef — the backend
  // accepts either, and this is the one a creator can retype from memory.
  const communityUrl = page?.slug
    ? `https://plus.heyjoe.io/creators/${page.slug}?ref=${page.slug}`
    : '';
  const copyCommunityUrl = () => {
    if (communityUrl) {
      navigator.clipboard.writeText(communityUrl);
      toast({ title: 'Community link copied', description: 'Share this with your own audience — sales through it are charged a lower commission.' });
    }
  };

  const editProduct = (product: CreatorProduct) => {
    if (product.productType === 'series') navigate(`/my-store/course/${product._id}`);
    else if (product.productType === 'bundle') navigate(`/my-store/bundle/${product._id}`);
    else if (product.productType === 'goods') navigate(`/my-store/product/${product._id}`);
    else if (product.productType === 'webinar') navigate(`/my-store/webinar/${product._id}`);
    else navigate(`/my-store/video/${product._id}`);
  };

  const upgradePill = (
    <button
      type="button"
      onClick={() => activateCreator()}
      className="inline-flex items-center gap-1.5"
      style={{ padding: '11px 22px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer', minHeight: 44 }}
    >
      Start selling — Free
    </button>
  );

  if (!isAuthenticated || subLoading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  if (!isSubscriber) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
        <div className="text-center" style={{ maxWidth: 460 }}>
          <h1 className="font-hj-display m-0" style={{ fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 800 }}>Become a Hey Joe Creator.</h1>
          <p style={{ marginTop: 12, fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.55 }}>
            It's free — activate your Creator account to build your store and sell on the marketplace. We take a small commission on sales.
          </p>
          <div style={{ marginTop: 22 }}>{upgradePill}</div>
        </div>
      </main>
    );
  }

  if (!canSellProducts) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
        <div className="text-center" style={{ maxWidth: 460 }}>
          <h1 className="font-hj-display m-0" style={{ fontSize: 28, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 800 }}>Start selling on the marketplace.</h1>
          <p style={{ marginTop: 12, fontSize: 14, color: 'var(--hj-ink)', lineHeight: 1.55 }}>
            Selling is free — activate your Creator account to build your store, sell content, and register as a reader. We take a small commission on sales.
          </p>
          <div style={{ marginTop: 22 }}>{upgradePill}</div>
        </div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  const ghostBtn: React.CSSProperties = {
    padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)',
    color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
    textDecoration: 'none', minHeight: 36, display: 'inline-flex', alignItems: 'center', gap: 6,
  };
  const primaryPill: React.CSSProperties = {
    padding: '8px 16px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)',
    color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
    minHeight: 38, display: 'inline-flex', alignItems: 'center', gap: 6,
  };
  const bannerSrc = bannerImage ? (bannerImage.startsWith('http') || bannerImage.startsWith('/api/') ? bannerImage : `/api/s3/${bannerImage}`) : null;

  // Type badge + meta for a catalog row.
  const productPill = (p: CreatorProduct): { label: string; tint: string; ink: string; Icon: typeof Radio } => {
    if (p.productType === 'webinar') return { label: 'Featured event', tint: 'var(--hj-pink-tint)', ink: 'var(--hj-pink)', Icon: Radio };
    if (p.productType === 'goods') {
      const g = p.goods;
      const left = g?.options?.length ? g.options.reduce((s, o) => s + Math.max(0, o.stock || 0), 0) : g?.stock;
      return { label: g?.kind === 'digital' ? 'Download' : `Product${left === null || left === undefined ? '' : left === 0 ? ' · sold out' : ` · ${left} left`}`, tint: 'var(--hj-yellow-tint)', ink: 'var(--hj-yellow)', Icon: Package };
    }
    if (p.productType === 'bundle') {
      const pass = (p.bundle as { mode?: string } | undefined)?.mode === 'pass';
      return { label: pass ? 'Season pass' : `Bundle · ${p.includedProductIds?.length || 0} listings`, tint: 'var(--hj-yellow-tint)', ink: 'var(--hj-yellow)', Icon: Package };
    }
    if (p.productType === 'series') {
      const lessons = p.modules?.reduce((s, m) => s + (m.lessons?.length || 0), 0) || p.videos.length;
      return { label: `Course · ${lessons} lesson${lessons !== 1 ? 's' : ''}`, tint: 'var(--hj-yellow-tint)', ink: 'var(--hj-yellow)', Icon: Layers };
    }
    const eps = p.videos.length;
    // A video product marked format:'series' (or just carrying >1 episode) is a series.
    const isVideoSeries = p.format === 'series' || eps > 1;
    return { label: isVideoSeries ? `Series · ${eps} ep${eps !== 1 ? 's' : ''}` : 'Video', tint: 'rgb(65 113 249 / 0.14)', ink: 'var(--hj-deep-blue)', Icon: Video };
  };

  return (
    <main className="mx-auto" style={{ maxWidth: 880, padding: '0 16px 96px', background: 'var(--hj-bg)', color: 'var(--hj-ink)', minHeight: 'calc(100vh - 68px)' }}>
      {/* Breadcrumb */}
      <div className="flex items-center gap-2.5" style={{ padding: '20px 0 10px', fontSize: 13 }}>
        <Link to="/" className="inline-flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)', textDecoration: 'none' }}>
          <ChevronLeft size={12} strokeWidth={2} /> Home
        </Link>
        <span style={{ color: 'var(--hj-ink-4)' }}>/</span>
        <span style={{ color: 'var(--hj-ink)' }}>My Store</span>
      </div>

      {!page || !String(page.displayName || '').trim() ? (
        <section
          style={{
            marginBottom: 20, padding: '18px 20px', borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-beige)', border: '1.5px solid var(--hj-beige-ink)',
            color: 'var(--hj-beige-ink)', display: 'grid', gap: 10,
          }}
        >
          <div className="mono-label" style={{ color: 'var(--hj-beige-ink)', letterSpacing: '0.12em' }}>
            Step 1 of 1
          </div>
          <p className="m-0" style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em' }}>
            Name your store to appear in the marketplace.
          </p>
          <p className="m-0" style={{ fontSize: 14.5, lineHeight: 1.55 }}>
            The marketplace is a list of stores. Until yours has a name, anything you publish
            works on its direct link but has nowhere to show up for people browsing.
            It takes one field.
          </p>
          <button
            type="button"
            onClick={() => {
              setStorefrontOpen(true);
              setTimeout(() => document.getElementById('storefront')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
            }}
            style={{
              justifySelf: 'start', minHeight: 40, padding: '0 18px', borderRadius: 999,
              background: 'var(--hj-beige-ink)', color: 'var(--hj-beige)', border: 0,
              fontWeight: 700, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Name my store →
          </button>
        </section>
      ) : null}

      {/* Store banner */}
      <section style={{ marginBottom: 20 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
          <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Store banner</div>
          <div className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>16:5 · optional</div>
        </div>
        <div className="relative w-full overflow-hidden" style={{ aspectRatio: '16 / 5', borderRadius: 'var(--hj-r-md)', background: bannerSrc ? 'var(--hj-surface-2)' : 'radial-gradient(120% 140% at 85% 0%, oklch(0.34 0.045 72) 0%, transparent 60%), linear-gradient(135deg, oklch(0.19 0.01 65), oklch(0.28 0.03 70))', border: '1px solid var(--hj-hairline)' }}>
          {bannerSrc && <img src={bannerSrc} alt="Store banner" className="absolute inset-0 w-full h-full" style={{ objectFit: 'cover' }} />}
          <label className="cursor-pointer absolute" style={{ top: 12, right: 12 }}>
            <span className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: 'oklch(0.18 0.005 70 / 0.72)', color: '#fff', fontSize: 12, fontWeight: 600, backdropFilter: 'blur(4px)' }}>
              {uploadingBanner ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
              {bannerImage ? 'Change banner' : 'Add banner'}
            </span>
            <input type="file" accept="image/*" className="hidden" onChange={handleBannerUpload} disabled={uploadingBanner} />
          </label>
        </div>
        <p className="m-0" style={{ marginTop: 8, fontSize: 12.5, color: 'var(--hj-ink-2)' }}>
          Shown as your storefront hero and on your marketplace card. No banner? We fall back to the brand gradient.
          {' · '}<Link to="/my-store/studio" style={{ color: 'var(--hj-yellow-ink)', textDecoration: 'none', fontWeight: 600 }}>Make one in the studio →</Link>
          {bannerImage && <> · <button type="button" onClick={() => persistBanner('')} style={{ color: 'var(--hj-ink-3)', textDecoration: 'underline', background: 'none', border: 0, cursor: 'pointer', fontSize: 12.5 }}>Remove</button></>}
        </p>
      </section>

      {/* Header */}
      <header className="flex items-end justify-between gap-4 flex-wrap" style={{ padding: '4px 0 24px', borderBottom: '1px solid var(--hj-hairline-soft)', marginBottom: 24 }}>
        <div>
          <div className="mono-label" style={{ marginBottom: 10 }}>My Store</div>
          <h1 className="font-hj-display m-0" style={{ fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: 1, color: 'var(--hj-ink)', letterSpacing: '-0.025em', fontWeight: 700 }}>
            Sell what you know.
          </h1>
        </div>
        {page?.slug && (
          <div className="flex gap-2">
            <button type="button" onClick={copyStoreUrl} style={ghostBtn}><Copy className="w-3.5 h-3.5" /> Copy link</button>
            <Link to={`/creators/${page.slug}`} target="_blank" style={ghostBtn}><ExternalLink className="w-3.5 h-3.5" /> View page</Link>
          </div>
        )}
      </header>

      {communityUrl && (
        <div style={{ margin: '0 0 20px', padding: '14px 16px', borderRadius: 12, background: 'var(--hj-yellow)', color: '#0A0A0A' }}>
          <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 4 }}>Your community link</div>
          <div style={{ fontSize: 13, lineHeight: 1.45, marginBottom: 10 }}>
            Share this with your own audience (your list, socials, DMs). Sales through it are charged our lower own-community commission. Links from Hey Joe's own marketing stay at the standard rate.
          </div>
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <code style={{ fontSize: 12, background: 'rgba(0,0,0,0.08)', padding: '4px 8px', borderRadius: 6, wordBreak: 'break-all' }}>{communityUrl}</code>
            <button type="button" onClick={copyCommunityUrl} style={{ ...ghostBtn, background: '#0A0A0A', color: 'var(--hj-yellow-ink)' }}><Copy className="w-3.5 h-3.5" /> Copy community link</button>
          </div>
        </div>
      )}

      {page?.slug && <CreatorEmailSetup />}

      {/* Find Talent — live 2026-08-17. Creating a project happens HERE, in our
          own builder. The game-show gate below is separate and stays. */}
        <Link
          to="/find-talent"
          className="group flex items-center gap-4 no-underline"
          style={{
            marginBottom: 24,
            padding: '16px 20px',
            borderRadius: 'var(--hj-r-md)',
            background: 'var(--hj-beige)',
            border: '1px solid var(--hj-beige-line)',
            color: 'var(--hj-beige-ink)',
          }}
        >
          <div
            className="rounded-full flex items-center justify-center flex-shrink-0"
            style={{ width: 44, height: 44, background: 'oklch(0.18 0.005 70)', color: '#fff' }}
          >
            <Users className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 3 }}>Find talent</div>
            <p className="m-0" style={{ fontSize: 13.5, color: 'var(--hj-beige-ink)', lineHeight: 1.45 }}>
              Find and source talent for your next project from our creator database.
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1 mono-label flex-shrink-0 group-hover:gap-1.5 transition-all"
            style={{ color: 'var(--hj-beige-ink)', fontWeight: 700 }}
          >
            Start
            <ChevronRight size={13} strokeWidth={2.4} />
          </span>
        </Link>

      {/* Create */}
      <section style={{ marginBottom: 32 }}>
        <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Create</div>
        <h2 className="font-hj-display m-0" style={{ marginTop: 4, marginBottom: 16, fontSize: 20, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>
          What are you putting up for sale?
        </h2>
        {/* Game show card is DEV-ONLY until the format ships to prod — same
            hostname gate as the Find Talent entry above. */}
        <CreateTypeGrid hrefMode="type" includeGameShow={typeof window !== 'undefined' && (window.location.hostname.startsWith('dev-') || window.location.hostname === 'localhost')} />
      </section>

      {/* Series in development — drafts in the Series Studio, not yet published. */}
      {seriesDrafts.length > 0 && (
        <section className="flex flex-col gap-3" style={{ marginBottom: 24 }}>
          <div>
            <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Series studio</div>
            <h2 className="font-hj-display m-0" style={{ marginTop: 4, fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>In development.</h2>
          </div>
          <div className="flex flex-col gap-2">
            {seriesDrafts.map(p => {
              const eps = p.episodes?.length || 0;
              const scripted = (p.episodes || []).filter(e => (e.script || '').trim()).length;
              return (
                <Link key={p._id} to={`/my-store/series-studio/${p._id}`} className="flex items-center gap-3" style={{ padding: 14, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', textDecoration: 'none' }}>
                  <span className="inline-flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 10, background: 'rgb(65 113 249 / 0.12)', color: 'var(--hj-deep-blue)' }}><Video className="w-5 h-5" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="truncate" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{p.title || 'Untitled series'}</span>
                      {/* Same Draft pill as the catalog, so the tag reads identically across every draft type. */}
                      <span className="mono-label" style={{ padding: '2px 8px', borderRadius: 999, border: '1px solid oklch(0.65 0.16 75 / 0.5)', color: 'oklch(0.65 0.16 75)' }}>Draft</span>
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--hj-ink-3)', marginTop: 2 }}>Series · {eps} episode{eps !== 1 ? 's' : ''}{scripted ? ` · ${scripted} scripted` : ''}</div>
                  </div>
                  <span className="mono-label inline-flex items-center gap-1" style={{ color: 'var(--hj-ink-3)', flexShrink: 0 }}>Continue <ChevronRight className="w-3.5 h-3.5" /></span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Product orders: what to ship where, and Mark shipped (releases the money). */}
      <StoreOrdersPanel />

      {/* Catalog */}
      <section className="flex flex-col gap-3" style={{ marginBottom: 24 }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>Catalog</div>
            <h2 className="font-hj-display m-0" style={{ marginTop: 4, fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>Your listings.</h2>
          </div>
          <Link to="/my-store/new" style={ghostBtn}><Plus className="w-3.5 h-3.5" /> New listing</Link>
        </div>

        {products.length === 0 ? (
          <div className="text-center" style={{ padding: '48px 24px', borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1.5px dashed var(--hj-hairline)', color: 'var(--hj-ink-3)', fontSize: 14 }}>
            Nothing yet. Pick a type above to create your first listing.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {products.map(product => {
              const pill = productPill(product);
              const cover = product.coverImage
                ? (product.coverImage.startsWith('http') || product.coverImage.startsWith('/api/') ? product.coverImage : `/api/s3/${product.coverImage}`)
                : product.videos[0]?.thumbnailUrl
                  ? (product.videos[0].thumbnailUrl.startsWith('http') ? product.videos[0].thumbnailUrl : `/api/s3/${product.videos[0].thumbnailUrl}`)
                  : null;
              const meta = product.productType === 'webinar' && product.eventDate
                ? new Date(product.eventDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : null;
              return (
                <div key={product._id} className="flex items-center gap-4" style={{ padding: 14, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
                  <div className="rounded-md flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ width: 80, height: 56, background: 'var(--hj-surface-2)' }}>
                    {cover ? <img src={cover} alt={product.title} className="w-full h-full object-cover" /> : <pill.Icon className="w-5 h-5" style={{ color: pill.ink }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* The title opens the product as buyers see it (drafts render
                          for the owner); the pencil is the editor. */}
                      <button type="button" onClick={() => navigate(`/course/${product._id}`)} title="Preview" className="truncate m-0 text-left"
                        style={{ fontSize: 14, fontWeight: 600, color: 'var(--hj-ink)', background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: 'inherit', maxWidth: '100%' }}>
                        {product.title}
                      </button>
                      {product.reviewStatus === 'pending' ? (
                        <span className="mono-label" title="Your first live event is being reviewed before it goes live." style={{ padding: '2px 8px', borderRadius: 999, border: '1px solid oklch(0.7 0.15 250 / 0.5)', color: 'oklch(0.6 0.15 250)' }}>In review</span>
                      ) : product.reviewStatus === 'rejected' ? (
                        <span className="mono-label" title={product.reviewReason || 'Needs changes before it can go live.'} style={{ padding: '2px 8px', borderRadius: 999, border: '1px solid hsl(var(--destructive) / 0.5)', color: 'hsl(var(--destructive))' }}>Needs changes</span>
                      ) : !product.published ? (
                        <span className="mono-label" style={{ padding: '2px 8px', borderRadius: 999, border: '1px solid oklch(0.65 0.16 75 / 0.5)', color: 'oklch(0.65 0.16 75)' }}>Draft</span>
                      ) : null}
                      <span className="mono-label inline-flex items-center gap-1" style={{ padding: '2px 8px', borderRadius: 999, background: pill.tint, color: pill.ink }}>
                        <pill.Icon className="w-3 h-3" /> {pill.label}
                      </span>
                    </div>
                    <p className="m-0" style={{ marginTop: 4, fontSize: 12, color: 'var(--hj-ink-3)' }}>
                      {meta && <>{meta} · </>}
                      <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', color: 'var(--hj-ink-2)' }}>
                        ${(product.price / 100).toFixed(2)}{product.pricingMode === 'subscription' ? '/mo' : ''}
                      </span>
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => editProduct(product)} title="Edit" aria-label="Edit product" style={{ width: 32, height: 32, borderRadius: 999, background: 'transparent', border: 0, color: 'var(--hj-ink-3)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button type="button" onClick={() => handleDeleteProduct(product._id)} title="Delete" aria-label="Delete product" style={{ width: 32, height: 32, borderRadius: 999, background: 'transparent', border: 0, color: 'hsl(var(--destructive))', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Payouts (collapsible) */}
      {page && (
        <CollapsibleSection
          kicker="Payouts"
          kickerIcon={<CreditCard className="w-3.5 h-3.5" />}
          title="Get paid via Stripe."
          summary={
            connectStatus?.onboarded
              ? <span className="inline-flex items-center gap-1.5" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)' }}><CheckCircle className="w-3.5 h-3.5" style={{ color: 'var(--hj-green)' }} /> Connected</span>
              : connectStatus?.connected
                ? <span className="inline-flex items-center gap-1.5" style={{ fontSize: 12.5, color: 'var(--hj-ink-3)' }}><AlertCircle className="w-3.5 h-3.5" style={{ color: 'oklch(0.65 0.16 75)' }} /> Incomplete</span>
                : <span className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>Not connected</span>
          }
          open={payoutsOpen}
          onToggle={() => setPayoutsOpen(o => !o)}
        >
          {(() => {
            if (connectStatus?.onboarded) {
              // Stripe's default is a deposit every day there's a sale. Fine for a
              // steady trickle, miserable for anyone who sells 40 tickets to one
              // class and then has 40 deposits to reconcile.
              const interval = connectStatus.payoutSchedule?.interval || 'daily';
              const options: { key: 'daily' | 'weekly' | 'monthly'; label: string; note: string }[] = [
                { key: 'daily', label: 'Daily', note: 'Paid out as sales clear.' },
                { key: 'weekly', label: 'Weekly', note: 'One deposit each Friday.' },
                { key: 'monthly', label: 'Monthly', note: 'One deposit on the 1st.' },
              ];
              const choose = async (key: 'daily' | 'weekly' | 'monthly') => {
                if (key === interval || schedSaving) return;
                setSchedSaving(key);
                try {
                  const applied = await setPayoutSchedule(key, key === 'weekly' ? { weeklyAnchor: 'friday' } : key === 'monthly' ? { monthlyAnchor: 1 } : {});
                  setConnectStatus(prev => (prev ? { ...prev, payoutSchedule: applied } : prev));
                  toast({ title: 'Payout schedule updated', description: options.find(o => o.key === key)?.note });
                } catch (err: any) {
                  toast({ title: 'Could not update payouts', description: err.message, variant: 'destructive' });
                } finally {
                  setSchedSaving(null);
                }
              };
              return (
                <div className="flex flex-col gap-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--hj-ink-2)' }}>
                      <CheckCircle className="w-4 h-4" style={{ color: 'var(--hj-green)' }} />
                      <span>Stripe connected — you receive 80% of each sale automatically.</span>
                    </div>
                    <button type="button" style={ghostBtn} onClick={async () => { try { const url = await getConnectDashboardUrl(); window.open(url, '_blank'); } catch (err: any) { toast({ title: 'Failed', description: err.message, variant: 'destructive' }); } }}>
                      <ExternalLink className="w-3.5 h-3.5" /> Stripe dashboard
                    </button>
                  </div>

                  <div style={{ borderTop: '1px solid var(--hj-hairline)', paddingTop: 14 }}>
                    <p className="m-0" style={{ fontSize: 13, fontWeight: 600, color: 'var(--hj-ink)' }}>How often you get paid</p>
                    <p className="m-0" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', marginTop: 3 }}>
                      This only changes how many deposits hit your bank, not what you earn or when a sale lands in Stripe. Sell a lot of tickets to one class and daily payouts turn into a pile of small deposits to reconcile.
                    </p>
                    <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
                      {options.map(o => {
                        const active = o.key === interval;
                        return (
                          <button
                            key={o.key}
                            type="button"
                            onClick={() => choose(o.key)}
                            disabled={!!schedSaving}
                            aria-pressed={active}
                            title={o.note}
                            className="disabled:cursor-not-allowed"
                            style={{
                              ...(active ? primaryPill : ghostBtn),
                              opacity: schedSaving && schedSaving !== o.key ? 0.5 : 1,
                            }}
                          >
                            {schedSaving === o.key && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="m-0" style={{ fontSize: 12, color: 'var(--hj-ink-2)', marginTop: 8 }}>
                      {options.find(o => o.key === interval)?.note}
                    </p>
                  </div>
                </div>
              );
            }
            if (connectStatus?.connected) {
              return (
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-2" style={{ fontSize: 13, color: 'var(--hj-ink-2)' }}>
                    <AlertCircle className="w-4 h-4" style={{ color: 'oklch(0.65 0.16 75)' }} />
                    <span>Stripe account created but onboarding incomplete.</span>
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                  <PayoutCountrySelect value={payoutCountry || defaultPayoutCountry(connectStatus?.country)} onChange={setPayoutCountry} disabled={connectLoading} />
                  <button type="button" disabled={connectLoading} className="disabled:cursor-not-allowed" style={{ ...primaryPill, opacity: connectLoading ? 0.6 : 1 }}
                    onClick={async () => { setConnectLoading(true); try { const url = await startConnectOnboarding(payoutCountry || defaultPayoutCountry(connectStatus?.country)); window.location.href = url; } catch (err: any) { toast({ title: 'Failed', description: err.message, variant: 'destructive' }); setConnectLoading(false); } }}>
                    {connectLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Continue setup
                  </button>
                  </div>
                </div>
              );
            }
            return (
              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="m-0" style={{ fontSize: 13, color: 'var(--hj-ink-2)' }}>Connect your Stripe account to receive payouts (80% of each sale).</p>
                <div className="flex items-center gap-3 flex-wrap">
                <PayoutCountrySelect value={payoutCountry || defaultPayoutCountry(connectStatus?.country)} onChange={setPayoutCountry} disabled={connectLoading} />
                <button type="button" disabled={connectLoading} className="disabled:cursor-not-allowed" style={{ ...primaryPill, opacity: connectLoading ? 0.6 : 1 }}
                  onClick={async () => { setConnectLoading(true); try { const url = await startConnectOnboarding(payoutCountry || defaultPayoutCountry(connectStatus?.country)); window.location.href = url; } catch (err: any) { toast({ title: 'Failed', description: err.message, variant: 'destructive' }); setConnectLoading(false); } }}>
                  {connectLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Connect Stripe
                </button>
                </div>
              </div>
            );
          })()}
        </CollapsibleSection>
      )}

      {/* Storefront & sales (collapsible) */}
      <div id="storefront" />
      <CollapsibleSection
        kicker="Storefront & sales"
        kickerIcon={<BarChart3 className="w-3.5 h-3.5" />}
        title="Your page & earnings."
        summary={sales && sales.totalSales > 0 ? <span className="tabular" style={{ fontSize: 12.5, color: 'var(--hj-ink-2)', fontFamily: 'var(--hj-font-mono)' }}>${(sales.creatorRevenue / 100).toFixed(2)} earned</span> : <span className="mono-label" style={{ color: 'var(--hj-ink-4)' }}>Edit page</span>}
        open={storefrontOpen}
        onToggle={() => setStorefrontOpen(o => !o)}
      >
        {/* Page settings */}
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Display Name *</Label>
              <Input value={displayName} onChange={e => { setDisplayName(e.target.value); setFormErrors(e2 => ({ ...e2, displayName: '' })); }} placeholder="Your name" required />
              {formErrors.displayName && <p className="text-xs text-destructive">{formErrors.displayName}</p>}
            </div>
            <div className="space-y-2">
              <Label>URL Slug</Label>
              <div className="flex items-center gap-1">
                <span className="text-sm text-muted-foreground whitespace-nowrap">/creators/</span>
                <Input value={slugInput} onChange={e => setSlugInput(e.target.value)} placeholder="your-name" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Bio</Label>
            <Textarea value={bio} onChange={e => setBio(e.target.value)} placeholder="Tell buyers about yourself..." rows={3} />
          </div>
          <div className="space-y-2">
            <Label>Profile Image</Label>
            <div className="flex items-center gap-4">
              {imageUrl ? (
                <img src={imageUrl.startsWith('http') || imageUrl.startsWith('/api/') ? imageUrl : `/api/s3/${imageUrl}`} alt="Profile" className="w-16 h-16 hj-headshot object-cover ring-2 ring-border" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center ring-2 ring-border"><ImageIcon className="w-6 h-6 text-muted-foreground" /></div>
              )}
              <div>
                <label className="cursor-pointer">
                  <Button variant="outline" size="sm" asChild disabled={uploadingImage}>
                    <span>{uploadingImage ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}{imageUrl ? 'Change Photo' : 'Upload Photo'}</span>
                  </Button>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                </label>
                <p className="text-xs text-muted-foreground mt-1">JPG or PNG, max 5MB</p>
              </div>
            </div>
          </div>
          <button type="button" onClick={savePage} disabled={saving} className="inline-flex items-center gap-1.5 self-start disabled:cursor-not-allowed"
            style={{ padding: '9px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, minHeight: 40 }}>
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{page ? 'Save changes' : 'Create page'}
          </button>
        </div>

        {/* Sales dashboard */}
        {sales && (sales.totalSales > 0 || (sales.subscriptionMetrics && sales.subscriptionMetrics.activeSubscribers > 0) || (sales.monthlyTrends && sales.monthlyTrends.length > 0)) && (
          <div className="flex flex-col gap-5" style={{ marginTop: 24, paddingTop: 24, borderTop: '1px solid var(--hj-hairline-soft)' }}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { label: 'Total sales', value: sales.totalSales },
                { label: 'Gross revenue', value: `$${(sales.totalRevenue / 100).toFixed(2)}` },
                { label: 'Your earnings (80%)', value: `$${(sales.creatorRevenue / 100).toFixed(2)}`, accent: true },
              ].map(stat => (
                <div key={stat.label} className="text-center" style={{ padding: 14, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' }}>
                  <p className="tabular m-0" style={{ fontSize: 24, fontWeight: 700, color: stat.accent ? 'var(--hj-blue)' : 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>{stat.value}</p>
                  <p className="mono-label m-0" style={{ marginTop: 4, color: 'var(--hj-ink-3)' }}>{stat.label}</p>
                </div>
              ))}
            </div>

            {sales.productStats.length > 1 && (
              <div>
                <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>By product</div>
                <div className="flex flex-col">
                  {sales.productStats.map((ps, i) => (
                    <div key={ps.productId} className="flex items-center justify-between" style={{ padding: '8px 0', borderBottom: i < sales.productStats.length - 1 ? '1px solid var(--hj-hairline-soft)' : 'none', fontSize: 13 }}>
                      <span className="truncate flex-1" style={{ color: 'var(--hj-ink)' }}>{ps.title}</span>
                      <span className="mono-label mx-3 tabular" style={{ color: 'var(--hj-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>{ps.salesCount} sale{ps.salesCount !== 1 ? 's' : ''}</span>
                      <span className="tabular" style={{ fontFamily: 'var(--hj-font-mono)', fontWeight: 600, color: 'var(--hj-ink)' }}>${(ps.revenue / 100).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sales.recentSales.length > 0 && (
              <div>
                <div className="mono-label" style={{ marginBottom: 8, color: 'var(--hj-ink-3)' }}>Recent sales</div>
                <div className="overflow-y-auto" style={{ maxHeight: 192 }}>
                  {sales.recentSales.slice(0, 10).map((s, i, arr) => (
                    <div key={s._id} className="flex items-center justify-between" style={{ padding: '8px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--hj-hairline-soft)' : 'none', fontSize: 13 }}>
                      <div className="flex-1 min-w-0">
                        <p className="truncate m-0" style={{ color: 'var(--hj-ink)' }}>{s.productTitle}</p>
                        <p className="m-0 tabular" style={{ marginTop: 2, fontSize: 11, color: 'var(--hj-ink-3)', fontFamily: 'var(--hj-font-mono)' }}>{s.buyerEmail} · {new Date(s.purchasedAt).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right" style={{ marginLeft: 12 }}>
                        <p className="m-0 tabular" style={{ fontWeight: 700, color: 'var(--hj-yellow-ink)', fontFamily: 'var(--hj-font-mono)' }}>+${(s.creatorShare / 100).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {sales.subscriptionMetrics && sales.subscriptionMetrics.activeSubscribers > 0 && (
              <div>
                <div className="mono-label flex items-center gap-1.5" style={{ marginBottom: 10, color: 'var(--hj-ink-3)' }}><Users className="w-3.5 h-3.5" /> Subscription metrics</div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(() => {
                    const m = sales.subscriptionMetrics;
                    const churnColor = m.churn.rate > 0.10 ? 'hsl(var(--destructive))' : m.churn.rate > 0.05 ? 'oklch(0.65 0.16 75)' : 'var(--hj-blue)';
                    const tiles = [
                      { label: 'Active subscribers', value: m.activeSubscribers },
                      { label: 'MRR', value: `$${(m.mrr / 100).toFixed(2)}`, accent: 'var(--hj-blue)' },
                      { label: 'Churn (30d)', value: `${Math.round(m.churn.rate * 100)}%`, accent: churnColor },
                      { label: 'Avg LTV', value: `$${(m.ltv / 100).toFixed(2)}` },
                    ];
                    return tiles.map(t => (
                      <div key={t.label} className="text-center" style={{ padding: 12, borderRadius: 'var(--hj-r-sm)', background: 'var(--hj-surface-2)', border: '1px solid var(--hj-hairline-soft)' }}>
                        <p className="tabular m-0" style={{ fontSize: 22, fontWeight: 700, color: t.accent || 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>{t.value}</p>
                        <p className="mono-label m-0" style={{ marginTop: 3, color: 'var(--hj-ink-3)' }}>{t.label}</p>
                      </div>
                    ));
                  })()}
                </div>
              </div>
            )}

            {sales.monthlyTrends && sales.monthlyTrends.length > 0 && (() => {
              const maxRevenue = Math.max(...sales.monthlyTrends.map(t => t.revenue), 1);
              return (
                <div>
                  <div className="mono-label flex items-center gap-1.5" style={{ marginBottom: 10, color: 'var(--hj-ink-3)' }}><TrendingUp className="w-3.5 h-3.5" /> Monthly trends</div>
                  <div className="flex flex-col gap-2.5">
                    {sales.monthlyTrends.map(t => {
                      const [year, month] = t.month.split('-');
                      const label = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
                      const barWidth = Math.max((t.revenue / maxRevenue) * 100, 2);
                      return (
                        <div key={t.month}>
                          <div className="flex items-center justify-between" style={{ fontSize: 12.5, marginBottom: 4 }}>
                            <span className="flex-shrink-0" style={{ width: 88, fontWeight: 600, color: 'var(--hj-ink)' }}>{label}</span>
                            <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>
                              {t.sales} sale{t.sales !== 1 ? 's' : ''}
                              {t.newSubscribers > 0 && <> · {t.newSubscribers} new sub{t.newSubscribers !== 1 ? 's' : ''}</>}
                              {t.cancellations > 0 && <> · {t.cancellations} churn</>}
                            </span>
                            <span className="tabular" style={{ marginLeft: 8, width: 80, textAlign: 'right', fontWeight: 600, color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }}>${(t.revenue / 100).toFixed(2)}</span>
                          </div>
                          <div className="w-full overflow-hidden" style={{ height: 6, borderRadius: 999, background: 'var(--hj-surface-2)' }}>
                            <div className="h-full transition-all" style={{ width: `${barWidth}%`, background: 'var(--hj-blue)', borderRadius: 999 }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </CollapsibleSection>
    </main>
  );
};

// Dark-page collapsible card — a summary row that expands its content in place.
function CollapsibleSection({
  kicker, kickerIcon, title, summary, open, onToggle, children,
}: {
  kicker: string;
  kickerIcon: React.ReactNode;
  title: string;
  summary: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)', marginBottom: 16, overflow: 'hidden' }}>
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-3" style={{ padding: 18, background: 'transparent', border: 0, cursor: 'pointer', textAlign: 'left' }} aria-expanded={open}>
        <div>
          <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)' }}>{kickerIcon} {kicker}</div>
          <div className="font-hj-display" style={{ marginTop: 4, fontSize: 18, color: 'var(--hj-ink)', letterSpacing: '-0.02em', fontWeight: 700 }}>{title}</div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {!open && summary}
          <ChevronDown className="w-4 h-4" style={{ color: 'var(--hj-ink-3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 160ms ease' }} />
        </div>
      </button>
      {open && <div style={{ padding: '0 18px 18px' }}>{children}</div>}
    </div>
  );
}

export default MyStorePage;
